import React, { FunctionComponent } from 'react';

import { connect, useDispatch } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import AppState from '@webapp/store/state';

import Flex from '@components/layout/flex';
import ValidationInput from './validation-input';
import CopyIcon from '@components/basics/icons/copy';
import Typography from '@components/basics/typography';
import SnackbarStatus from '@components/basics/snackbar/snackbar-status';
import CircleRightArrow from '@components/basics/icons/circle-right-arrow';
import { SnackbarContainer } from '@components/basics/snackbar/snackbar-container';

import * as auth from '@webapp/sdk/auth';
import { MarkAsVerified } from '../../../store/user/action-creators';

interface ValidationProps {
  className?: string;
  isAuthenticator?: boolean;
  isMobile?: boolean;
  address: string;
  isPrimary: boolean;
  type: 'email' | 'phone';
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

const Validation: FunctionComponent<ValidationProps> = ({
  className,
  isAuthenticator,
  isMobile,
  address,
  isPrimary,
  type,
}) => {
  const dispatch = useDispatch();

  const [nextStep, setNextStep] = React.useState(
    isAuthenticator ? false : true
  );

  const [verificationCode, setVerificationCode] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [waitCode, setWaitCode] = React.useState(false);
  const [vc1, vc2, vc3, vc4, vc5, vc6] = verificationCode.split('');
  const [vcRef1, setVCRef1] = React.useState<HTMLInputElement | null>();
  const [vcRef2, setVCRef2] = React.useState<HTMLInputElement | null>();
  const [vcRef3, setVCRef3] = React.useState<HTMLInputElement | null>();
  const [vcRef4, setVCRef4] = React.useState<HTMLInputElement | null>();
  const [vcRef5, setVCRef5] = React.useState<HTMLInputElement | null>();
  const [vcRef6, setVCRef6] = React.useState<HTMLInputElement | null>();

  const [showNotification, setShowNotification] = React.useState(false);

  React.useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  React.useEffect(() => {
    (async function () {
      if (verificationCode.length === 6) {
        setLoading(true);
        if (!isPrimary) {
          await auth.confirmAddress(address, verificationCode);
          dispatch(new MarkAsVerified(address));
        } else {
          if (type === 'email') {
            await auth.verifyEmailSubmit(verificationCode);
          } else {
            await auth.verifyPhoneSubmit(verificationCode);
          }
        }
        await auth.getUser(true);
      }
    })();
  }, [verificationCode, address, type, isPrimary, dispatch]);

  React.useEffect(() => {
    if (waitCode) {
      const timer = setTimeout(() => {
        setWaitCode(false);
      }, 15 * 1000);

      return () => clearTimeout(timer);
    }
  }, [waitCode]);

  const handleResendCode = async () => {
    setWaitCode(true);
    await auth.resendCodeToAddress(address, isPrimary, type);
  };

  const editVerificationCode = (position: number, value: string) => {
    const cleanValue = value.trim();
    if (isNaN(Number(cleanValue || 0))) {
      return;
    }
    const start = position - 1;
    const pre = verificationCode.slice(0, start);

    const newCode = `${pre}${cleanValue}`.slice(0, 6);
    setVerificationCode(newCode);

    switch (newCode.length) {
      case 0:
        return vcRef1?.focus();
      case 1:
        if (!cleanValue) {
          return vcRef1?.focus();
        }
        return vcRef2?.focus();
      case 2:
        if (!cleanValue) {
          return vcRef2?.focus();
        }
        return vcRef3?.focus();
      case 3:
        if (!cleanValue) {
          return vcRef3?.focus();
        }
        return vcRef4?.focus();
      case 4:
        if (!cleanValue) {
          return vcRef4?.focus();
        }
        return vcRef5?.focus();
      case 5:
        if (!cleanValue) {
          return vcRef5?.focus();
        }
        return vcRef6?.focus();
      default:
        return vcRef6?.focus();
    }
  };

  // TODO: DEVTEAM-845 - This code should be auto-generated
  const CODE = 'J5WH7KWHJHJSXXCH4C55893H985BD8TVJD93';

  // TODO: DEVTEAM-845 - Review this function
  const handleCopy = async () => {
    if (navigator?.clipboard) {
      await navigator.clipboard.writeText(CODE);
      setShowNotification(true);
    }
  };

  return (
    <>
      <Separator />
      <ValidationBox className={className || ''}>
        {!nextStep ? (
          <Flex justifyContent="space-between" alignItems="center" gap={20}>
            <AuthenticatorWrapper>
              <ValidationTitle weight="700">
                <Translate id="VALIDATION.AUTHENTICATOR.TITLE" />
              </ValidationTitle>
              <Flex justifyContent="flex-start" alignItems="center" gap={5}>
                <ValidationText className="no-width" type="small">
                  <Translate id="VALIDATION.AUTHENTICATOR.TEXT" />{' '}
                  {CODE.substring(0, 18) + '...'}
                </ValidationText>
                <StyledCopyIcon onClick={handleCopy} />
              </Flex>
            </AuthenticatorWrapper>
            {/* TODO: DEVTEAM-845 - QR must be auto-generated */}
            {!isMobile && <QRPlaceHolder />}
            <StyledArrow onClick={() => setNextStep(true)} />
          </Flex>
        ) : (
          <>
            <ValidationText type="small">
              <Translate id="VALIDATION.TEXT" />
            </ValidationText>
            <ValidationWrapper>
              <ValidationInputs>
                <ValidationInput
                  disabled={loading}
                  forwardedRef={(el) => setVCRef1(el)}
                  onChange={(value) =>
                    editVerificationCode(1, value.target.value)
                  }
                  value={vc1 || ''}
                  invalid={!vc1}
                />
                <ValidationInput
                  disabled={loading}
                  forwardedRef={(el) => setVCRef2(el)}
                  onChange={(value) =>
                    editVerificationCode(2, value.target.value)
                  }
                  value={vc2 || ''}
                />
                <ValidationInput
                  disabled={loading}
                  forwardedRef={(el) => setVCRef3(el)}
                  onChange={(value) =>
                    editVerificationCode(3, value.target.value)
                  }
                  value={vc3 || ''}
                />
                <ValidationInput
                  disabled={loading}
                  forwardedRef={(el) => setVCRef4(el)}
                  onChange={(value) =>
                    editVerificationCode(4, value.target.value)
                  }
                  value={vc4 || ''}
                />
                <ValidationInput
                  disabled={loading}
                  forwardedRef={(el) => setVCRef5(el)}
                  onChange={(value) =>
                    editVerificationCode(5, value.target.value)
                  }
                  value={vc5 || ''}
                />
                <ValidationInput
                  disabled={loading}
                  forwardedRef={(el) => setVCRef6(el)}
                  onChange={(value) =>
                    editVerificationCode(6, value.target.value)
                  }
                  value={vc6 || ''}
                />
              </ValidationInputs>
              {!loading && !waitCode ? (
                <Typography
                  className="validation-link"
                  weight="700"
                  onClick={handleResendCode}
                >
                  <Translate id="VALIDATION.BUTTON" />
                </Typography>
              ) : null}
            </ValidationWrapper>
          </>
        )}
      </ValidationBox>
      {showNotification ? (
        <SnackbarContainer>
          <SnackbarStatus
            status="success"
            title={
              <Translate id="USER_PROFILE.SECURITY.PASSWORD.2FA.COPY.TEXT" />
            }
            onClose={() => setShowNotification(false)}
          />
        </SnackbarContainer>
      ) : null}
    </>
  );
};

export default connect(mapStateToProps)(Validation);

const ValidationBox = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 0 0 4px 4px;
  padding: 20px;
`;

const ValidationTitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.differentBackground};
`;

const ValidationText = styled(Typography)`
  color: ${({ theme }) => theme.colors.tertiary};
  max-width: none;
  @media ${({ theme }) => theme.device.tablet} {
    max-width: 324px;
    &.no-width {
      max-width: none;
    }
  }
`;

const StyledCopyIcon = styled(CopyIcon)`
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  @media ${({ theme }) => theme.device.tablet} {
    width: 16px;
    height: 16px;
  }
`;

const ValidationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;

  @media ${({ theme }) => theme.device.laptopL} {
    flex-direction: row;
  }

  .validation-link {
    cursor: pointer;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.differentBackground};
    opacity: 1;
    transition: opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
    &:hover {
      opacity: 0.5;
    }
  }
`;

const ValidationInputs = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  margin-top: 16px;
  @media ${({ theme }) => theme.device.laptopL} {
    width: auto;
    justify-content: flex-start;
  }
`;

const Separator = styled.div`
  height: 3px;
  background-color: ${({ theme }) => theme.colors.gray};
`;

const AuthenticatorWrapper = styled.div`
  display: grid;
  grid-gap: 5px;
`;

const QRPlaceHolder = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  width: 74px;
  height: 74px;
  object-fit: cover;
  border-radius: 2px;
`;

const StyledArrow = styled(CircleRightArrow)`
  width: 24px;
  height: 24px;
`;
